import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import type { components } from '@/api/schema'

type Schemas = components['schemas']

function n(value: number | string): number {
  return typeof value === 'number' ? value : Number(value)
}

function nn(value: number | string | null): number | null {
  return value === null ? null : n(value)
}

export interface BillingPlan {
  readonly code: string
  readonly monthlyPrice: number | null
  readonly selfServe: boolean
  readonly subscribable: boolean
  readonly entitlements: Readonly<Record<string, number>>
}

export interface Subscription {
  readonly id: string
  readonly planCode: string
  readonly monthlyPrice: number
  readonly status: string
  readonly currentPeriodStart: string | null
  readonly currentPeriodEnd: string | null
  readonly cancelAtPeriodEnd: boolean
  readonly scheduledPlanCode: string | null
}

export interface BillingProfile {
  readonly legalName: string
  readonly taxIdMasked: string
  readonly email: string
}

export interface Invoice {
  readonly id: string
  readonly number: string
  readonly kind: string
  readonly amount: number
  readonly currency: string
  readonly dueDate: string
  readonly status: string
  readonly paymentUrl: string | null
  readonly paidAt: string | null
  readonly createdAt: string
  readonly pix: { readonly qrCodeBase64: string | null; readonly copyPaste: string | null } | null
}

export interface BillingOverview {
  readonly state: string
  readonly plan: BillingPlan | null
  readonly subscription: Subscription | null
  readonly profile: BillingProfile | null
  readonly openInvoice: Invoice | null
  readonly plans: readonly BillingPlan[]
  readonly creditPacks: readonly CreditPack[]
  readonly currency: string
  readonly salesEmail: string
  readonly canManage: boolean
  readonly gatewayConfigured: boolean
}

export interface CreditPack {
  readonly code: string
  readonly credits: number
  readonly price: number
}

export interface InvoiceList {
  readonly items: readonly Invoice[]
  readonly total: number
}

function mapPlan(dto: Schemas['BillingPlanView']): BillingPlan {
  return {
    code: dto.code,
    monthlyPrice: nn(dto.monthlyPrice),
    selfServe: dto.selfServe,
    subscribable: dto.subscribable,
    entitlements: Object.fromEntries(Object.entries(dto.entitlements).map(([k, v]) => [k, n(v)])),
  }
}

function mapSubscription(dto: Schemas['SubscriptionView']): Subscription {
  return {
    id: dto.id,
    planCode: dto.planCode,
    monthlyPrice: n(dto.monthlyPrice),
    status: dto.status,
    currentPeriodStart: dto.currentPeriodStart,
    currentPeriodEnd: dto.currentPeriodEnd,
    cancelAtPeriodEnd: dto.cancelAtPeriodEnd,
    scheduledPlanCode: dto.scheduledPlanCode ?? null,
  }
}

function mapInvoice(dto: Schemas['InvoiceView']): Invoice {
  return {
    id: dto.id,
    number: dto.number,
    kind: dto.kind,
    amount: n(dto.amount),
    currency: dto.currency,
    dueDate: dto.dueDate,
    status: dto.status,
    paymentUrl: dto.paymentUrl,
    paidAt: dto.paidAt,
    createdAt: dto.createdAt,
    pix:
      dto.pix === null
        ? null
        : { qrCodeBase64: dto.pix.qrCodeBase64, copyPaste: dto.pix.copyPaste },
  }
}

function fail(error: unknown, status: number): never {
  throw new ApiError(parseProblemDetails(error, status))
}

/** Visão de cobrança (`billing.view`). `null` = sem permissão (403). */
export async function getBillingOverview(tenantId: string): Promise<BillingOverview | null> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/billing', {
    params: { path: { tenantId } },
  })
  if (result.response.status === 403) {
    return null
  }
  if (!result.response.ok || result.data === undefined) {
    fail(result.error, result.response.status)
  }
  const dto = result.data
  return {
    state: dto.state,
    plan: dto.plan === null ? null : mapPlan(dto.plan),
    subscription: dto.subscription === null ? null : mapSubscription(dto.subscription),
    profile: dto.profile,
    openInvoice: dto.openInvoice === null ? null : mapInvoice(dto.openInvoice),
    plans: dto.catalog.plans.map(mapPlan),
    creditPacks: dto.catalog.creditPacks.map((p) => ({
      code: p.code,
      credits: n(p.credits),
      price: n(p.price),
    })),
    currency: dto.catalog.currency,
    salesEmail: dto.catalog.salesEmail,
    canManage: dto.canManage,
    gatewayConfigured: dto.gatewayConfigured,
  }
}

export async function updateBillingProfile(
  tenantId: string,
  body: { legalName: string; taxId: string; email: string },
): Promise<BillingProfile> {
  const result = await apiClient.PUT('/api/v1/tenants/{tenantId}/billing/profile', {
    params: { path: { tenantId } },
    body,
  })
  if (!result.response.ok || result.data === undefined) {
    fail(result.error, result.response.status)
  }
  return result.data
}

export async function subscribe(tenantId: string, planCode: string): Promise<Invoice> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/billing/subscription', {
    params: { path: { tenantId } },
    body: { planCode },
  })
  if (!result.response.ok || result.data === undefined) {
    fail(result.error, result.response.status)
  }
  return mapInvoice(result.data.invoice)
}

export async function cancelSubscription(tenantId: string): Promise<Subscription> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/billing/subscription/cancel', {
    params: { path: { tenantId } },
  })
  if (!result.response.ok || result.data === undefined) {
    fail(result.error, result.response.status)
  }
  return mapSubscription(result.data)
}

export async function listInvoices(tenantId: string): Promise<InvoiceList> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/billing/invoices', {
    params: { path: { tenantId }, query: { page: 1, pageSize: 20 } },
  })
  if (result.response.status === 403) {
    return { items: [], total: 0 }
  }
  if (!result.response.ok || result.data === undefined) {
    fail(result.error, result.response.status)
  }
  return { items: result.data.items.map(mapInvoice), total: n(result.data.total) }
}

export async function getInvoice(tenantId: string, invoiceId: string): Promise<Invoice> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/billing/invoices/{invoiceId}', {
    params: { path: { tenantId, invoiceId } },
  })
  if (!result.response.ok || result.data === undefined) {
    fail(result.error, result.response.status)
  }
  return mapInvoice(result.data)
}

export interface ChangePlanResult {
  readonly invoice: Invoice | null
  readonly scheduledPlanCode: string | null
}

export async function changePlan(tenantId: string, planCode: string): Promise<ChangePlanResult> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/billing/subscription/change-plan',
    {
      params: { path: { tenantId } },
      body: { planCode },
    },
  )
  if (!result.response.ok || result.data === undefined) {
    fail(result.error, result.response.status)
  }
  return {
    invoice: result.data.invoice === null ? null : mapInvoice(result.data.invoice),
    scheduledPlanCode: result.data.scheduledPlanCode,
  }
}

export async function orderCreditPack(tenantId: string, packCode: string): Promise<Invoice> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/billing/credit-packs/orders', {
    params: { path: { tenantId } },
    body: { packCode },
  })
  if (!result.response.ok || result.data === undefined) {
    fail(result.error, result.response.status)
  }
  return mapInvoice(result.data)
}
